import { useMediaQuery } from "@mui/material";
import { FC, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { EditV2Icon } from "../../../assets/img/icons";
import Button from "../../../components/Button";
import ModalForm, { Field, FieldType } from "../../../components/ModalForm";
import { Select } from "../../../components/OldSelect";
import {
    getUserQuestions,
    updateUserQuestions,
} from "../../../services/api/question";
import { Question } from "../../../types/Question";
import { QuestionAnwserType } from "../../../types/enums/QuestionAnwserType";
import { Timeslots } from "../../../types/enums/Timeslots";
import { getEnNumericMonthYear } from "../../../utils/date";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
} from "../../../utils/errors";
import { formatAnswer } from "../../../utils/question";
import styles from "./styles.module.scss";

type Props = {
    handleTabChange: (key: string) => void;
};

const KYC: FC<Props> = ({ handleTabChange }) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [answersInput, setAnswersInput] = useState<{ [key: string]: string }>(
        {}
    );
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    const isMobile = useMediaQuery("(max-width: 1050px)");

    const getType = (type: QuestionAnwserType): FieldType => {
        switch (type) {
            case QuestionAnwserType.STRING:
                return "text";
            case QuestionAnwserType.DATE:
                return "month";
            case QuestionAnwserType.BOOLEAN:
                return "select";
            case QuestionAnwserType.TIMESLOT:
                return "select";
            default:
                return "text";
        }
    };

    const onChange = (id: string, value: string) => {
        if (value.length > 190) {
            value = value.slice(0, 190);
        }
        setAnswersInput((prev) => ({ ...prev, [id]: value }));
        setFieldErrors((prev) => clearFieldError(prev, id));
    };

    const formFields: Field<FieldType>[] = questions.map((q) => {
        let field: any = {
            id: q.id,
            label: q.label,
            type: getType(q.type) as FieldType,
            value: answersInput[q.id] || "",
            onChange: (value: string) => onChange(q.id, value as string),
            error: fieldErrors[q.id],
        };

        if (q.type === QuestionAnwserType.BOOLEAN) {
            field.options = [
                { value: "", label: "-" },
                { value: "true", label: "Oui" },
                { value: "false", label: "Non" },
            ];
        } else if (q.type === QuestionAnwserType.TIMESLOT) {
            let options = [{ value: "", label: "-" }];
            for (const key in Timeslots) {
                options.push({ value: key, label: (Timeslots as any)[key] });
            }
            field.options = options;
        } else if (q.type === QuestionAnwserType.DATE) {
            if (answersInput[q.id]) {
                const date = new Date(answersInput[q.id]);
                field.value = getEnNumericMonthYear(date);
            } else {
                onChange(q.id, getEnNumericMonthYear(new Date()));
            }
        }

        return field;
    });

    const handleSubmit = async () => {
        const nextErrors: FieldErrors = {};
        for (const q of questions) {
            const answer = answersInput[q.id];
            if (answer === "" || answer === null || answer === undefined) {
                nextErrors[q.id] = REQUIRED_FIELD;
            }
        }
        setFieldErrors(nextErrors);
        if (
            notifyFieldErrors(nextErrors, {
                message: "Veuillez remplir toutes les réponses",
                type: "error",
            })
        ) {
            return;
        }

        await updateUserQuestions(
            Object.entries(answersInput).map(([questionId, answer]) => ({
                questionId,
                answer,
            }))
        );

        const updatedQuestions = questions.map((q) => ({
            ...q,
            answer: answersInput[q.id],
        }));
        setQuestions(updatedQuestions);

        setIsEditModalOpen(false);
        toast.success("Informations mises à jour avec succès");
    };

    useEffect(() => {
        getUserQuestions()
            .then((questionsResponse) => {
                const questions = questionsResponse ?? [];
                setQuestions(questions);
                setAnswersInput(
                    questions.reduce((acc, q) => {
                        if (q.answer) acc[q.id] = q.answer;
                        return acc;
                    }, {} as { [key: string]: string })
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    return (
        <div className={styles.container}>
            <ModalForm
                open={isEditModalOpen}
                handleClose={() => setIsEditModalOpen(false)}
                fields={formFields}
                onSubmit={handleSubmit}
                title={`Éditer les informations${
                    isMobile ? "" : " complémentaires"
                }`}
                size="medium"
            />
            <div className={styles.header}>
                {isMobile ? (
                    <Select
                        className={styles.selectTab}
                        value="questions"
                        onChanged={(value) => {
                            const key = value as string;
                            handleTabChange(key === "profil" ? "" : key);
                        }}
                    >
                        <option value="profil">Profil</option>
                        <option value="questions">Informations</option>
                        <option value="affichage">Affichage</option>
                    </Select>
                ) : (
                    <span className={styles.title}>
                        Informations complémentaires
                    </span>
                )}
                <Button
                    variant="outlined"
                    size="small"
                    label={isMobile ? "" : "Editer"}
                    LeftIcon={EditV2Icon}
                    onClick={() => setIsEditModalOpen(true)}
                />
            </div>
            <div className={styles.section}>
                {isLoading ? (
                    <span>Loading...</span>
                ) : questions.length === 0 ? (
                    <span>Aucune question disponible</span>
                ) : (
                    questions.map((q) => (
                        <div key={q.id} className={styles.question}>
                            <span className={styles.questionLabel}>
                                {q.label}
                            </span>
                            <span className={styles.questionAnswer}>
                                {formatAnswer(q)}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default KYC;
